import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import BookingForm from '../components/BookingForm';
import RoomDetails from '../components/RoomDetails';
import TabSection from '../components/TabSection';
import RelatedRooms from '../components/RelatedRooms';

const NewViewRoomDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  // URL Parameters
  const checkInDate = searchParams.get('checkInDate');
  const checkOutDate = searchParams.get('checkOutDate');
  const totalrooms = searchParams.get('totalrooms');
  const totalguest = searchParams.get('totalguest');
  const id = searchParams.get('id');

  // State Management
  const [roomDetails, setRoomDetails] = useState(null);
  const [relatedRooms, setRelatedRooms] = useState([]);
  const [error, setError] = useState(null);
  const [checkInDateState, setCheckInDate] = useState(
    checkInDate ? new Date(moment(checkInDate, 'DD-MM-YYYY')) : null
  );
  const [checkOutDateState, setCheckOutDate] = useState(
    checkOutDate ? new Date(moment(checkOutDate, 'DD-MM-YYYY')) : null
  );
  const [roomListings, setRoomListings] = useState([]);
  const [rooms, setRooms] = useState([
    { id: 1, count: parseInt(totalguest) > 0 ? parseInt(totalguest) : 1 }
  ]);

  // Helper Functions
  const formatDateForUrl = (date) => {
    if (typeof date === 'string') {
      return date;
    }
    return moment(date).format('DD-MM-YYYY');
  };

  const incrementCount = (roomId) => {
    setRooms(rooms.map(room => 
      room.id === roomId ? { ...room, count: room.count + 1 } : room
    ));
  };

  const decrementCount = (roomId) => {
    setRooms(rooms.map(room => 
      room.id === roomId && room.count > 1 ? { ...room, count: room.count - 1 } : room
    ));
  };

  const addRoom = () => {
    const newId = Math.max(...rooms.map(r => r.id)) + 1;
    setRooms([...rooms, { id: newId, count: 1 }]);
  };

  const deleteRoom = () => {
    if (rooms.length > 1) {
      setRooms(rooms.slice(0, -1));
    }
  };

  // Handlers
  const handleSearch = () => {
    const totalRooms = rooms.length;
    const totalGuests = rooms.reduce((total, room) => total + (room.count || 0), 0);
    
    if (totalGuests === 0) {
      alert('Please select at least one guest');
      return;
    }

    const checkInFormatted = checkInDate ? formatDateForUrl(checkInDate) : formatDateForUrl(new Date());
    const checkOutFormatted = checkOutDate ? formatDateForUrl(checkOutDate) : formatDateForUrl(new Date());

    const queryParams = new URLSearchParams({
      checkInDate: checkInFormatted,
      checkOutDate: checkOutFormatted,
      totalrooms: totalRooms,
      totalguest: totalGuests,
      id: id || ''
    }).toString();

    navigate(`/Viewrooms?${queryParams}`);
  };

  const handlePayment = async () => {
    const firstName = document.getElementById('firstName').value;
    const lastName = document.getElementById('lastName').value;
    const email = document.getElementById('emailHelp').value;
    const contact = document.getElementById('mobile-no').value;
    const gender = document.getElementById('Gender').value;
    const address = document.getElementById('Address').value;

    if (!firstName || !lastName || !email || !contact || !gender || !address) {
      alert('Please fill all guest details before proceeding to payment.');
      return;
    }

    try {
      const response = await axios.post(`https://api.abhyaranyan.com/api/Abhyaranyan/RoomDetailbyid?id=${id}`, {
        roomid: id,
        firstName,
        lastName,
        gender,
        phone: contact,
        emailAddress: email,
        address,
        amount: roomDetails.amount,
        checkInDate: roomDetails.checkInDate,
        checkOutDate: roomDetails.checkOutDate,
        totalPerson: totalguest,
        numberofRooms: totalrooms,
        paymentMode_Id: 0,
        razorpayKey: 'rzp_test_BXjJAJuQSCxc3X',
        paymentId: 'temp_id',
        isPaymentStatus: true,
        order_Id: 'temp_order_id',
      });

      if (response.data.succeeded) {
        const { amount } = response.data.data;
        const options = {
          key: 'rzp_test_BXjJAJuQSCxc3X',
          amount: amount * 100,
          currency: 'INR',
          name: 'Abhyaranyan',
          description: 'Room Booking',
          handler: function (paymentResponse) {
            alert('Payment successful! Payment ID: ' + paymentResponse.razorpay_payment_id);
            alert('Booking Confirmed successfully!');
          },
          prefill: {
            name: `${firstName} ${lastName}`,
            email,
            contact,
          },
          notes: {
            address,
          },
          theme: {
            color: '#3399cc',
          },
        };

        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      } else {
        alert('Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking failed:', error);
      alert('Booking failed. Please try again.');
    }
  };

  // Effects
  useEffect(() => {
    if (checkInDate && checkOutDate && id) {
      const formattedCheckInDate = moment(checkInDate, 'DD-MM-YYYY').format('YYYY-MM-DD');
      const formattedCheckOutDate = moment(checkOutDate, 'DD-MM-YYYY').format('YYYY-MM-DD');
      const fetchData = async () => {
        try {
          const response = await axios.get(`https://api.abhyaranyan.com/api/Abhyaranyan/RoomDetailbyid`, {
            params: {
              checkInDate: formattedCheckInDate,
              checkOutDate: formattedCheckOutDate,
              totalrooms,
              totalguest,
              id,
            },
          });
          setRoomDetails(response.data.data);
          if (response.data.data.roomsList && response.data.data.roomsList.length > 0) {
            const relatedRoomsList = response.data.data.roomsList
              .filter(room => room.id !== parseInt(id))
              .slice(0, 3);
            setRelatedRooms(relatedRoomsList);
          }
        } catch (err) {
          console.error(err);
          setError('Failed to fetch room details.');
        }
      };
      fetchData();
    }
  }, [checkInDate, checkOutDate, totalrooms, totalguest, id]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Loading and Error States
  if (error) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!roomDetails) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container my-5">
        <div className="row">
          <RoomDetails roomDetails={roomDetails} />
          <div className="col-lg-4">
            <BookingForm
              checkInDate={checkInDateState}
              checkOutDate={checkOutDateState}
              rooms={rooms}
              handleSearch={handleSearch}
              setCheckInDate={setCheckInDate}
              setCheckOutDate={setCheckOutDate}
              incrementCount={incrementCount}
              decrementCount={decrementCount}
              addRoom={addRoom}
              deleteRoom={deleteRoom}
            />
          </div>
        </div>

        <section className="single-section bg-inner small-section">
          <div className="container">
            <div className="row">
              <div className="col-12 small-section pb-0">
                <TabSection 
                  roomDetails={roomDetails}
                  roomListings={roomListings}
                  query={location.search}
                />
              </div>
            </div>
          </div>
        </section>

        <RelatedRooms
          relatedRooms={relatedRooms}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          totalrooms={totalrooms}
          totalguest={totalguest}
        />
      </div>
      <Footer />
    </div>
  );
};

export default NewViewRoomDetails;
